import React from 'react';
import { Button, Card } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import { DspValidationType } from '../../enums/DspValidationType';
import { DspPackageDetail } from '../../pages/dsp/graphql/queries/getPackageDetail';
import { PhoneOutlined, EnvironmentOutlined } from '@ant-design/icons';
import {formatTimestampToSecondsTime} from "../../utils/dateTimeUtils";
import DeliveryPhoneAndNavigate from "../../pages/delivery/delivering/components/DeliveryPhoneAndNavigate";
import {formatStopNumber} from "../../utils/stopNumberUtils";
import OrderNoteItem from '../OrderNoteItem/OrderNoteItem';
import {maskPhoneNumber} from "../../utils/phoneUtils";

interface DspPackageCardProps {
  packageDetail: DspPackageDetail;
  validationType: DspValidationType;
  onConfirm: () => void;
  onCancel: () => void;
}

const DspPackageCard: React.FC<DspPackageCardProps> = ({
  packageDetail,
  validationType,
  onConfirm,
  onCancel
}) => {
  const { t, i18n } = useTranslation();

  // 根据验证类型获取按钮文案
  const getButtonText = () => {
    switch (validationType) {
      case DspValidationType.POD_MODIFY:
        return t('pages.dsp.home.editPod');
      case DspValidationType.SIGN_RECEIPT:
        return t('pages.dsp.packageCard.confirmDelivery');
      case DspValidationType.EXCEPTION_REPORT:
        return t('pages.dsp.packageCard.reportException');
      default:
        return t('pages.dsp.packageScan.confirm');
    }
  };


  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden" 
      onTouchMove={(e) => e.stopPropagation()}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div 
        className="w-full max-w-md bg-white rounded-lg shadow-lg flex flex-col h-[65vh] max-h-[65vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部固定区域 */}
        <div className="p-4 flex-shrink-0">
          {/* 运单号 */}
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              {packageDetail.trackingNumber}
            </h2>
          </div>

          {/* 收件人信息 */}
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <span className="text-lg font-medium text-gray-800">
                {packageDetail.recipientName || t('common.unknown')}
              </span>
            </div>
            
            {packageDetail.recipientPhone && (
              <div className="flex items-center mb-2 text-gray-600">
                <PhoneOutlined className="mr-2" />
                <span>{maskPhoneNumber(packageDetail.recipientPhone)}</span>
              </div>
            )}
          </div>

          {/* 收件地址 */}
          {packageDetail.recipientAddress && (
            <div className="mb-4">
              <div className="flex items-start text-gray-600">
                <EnvironmentOutlined className="mr-2 mt-1 flex-shrink-0" />
                <span className="text-sm leading-relaxed">
                  {packageDetail.recipientAddress}
                </span>
              </div>
            </div>
          )}

          <DeliveryPhoneAndNavigate
            phone={packageDetail?.recipientPhone || ''}
            address={packageDetail?.recipientAddress || ''}
            tracking={packageDetail?.trackingNumber}
            latitude={packageDetail?.recipientLatitude}
            longitude={packageDetail?.recipientLongitude}
            stopNumber={packageDetail?.deliveryTaskOrder?.stopNumber?.toString()}
            fullName={packageDetail?.recipientName}
            subStopNumber={packageDetail?.deliveryTaskOrder?.subStopNumber}
          />
        </div>

        {/* 中间可滚动区域 - 从stop字段到备注 */}
        <div 
          className="px-4 overflow-y-auto flex-1" 
          style={{ 
            minHeight: 0,
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {/* 路线信息 */}
          {packageDetail.deliveryTaskOrder && (
            <Card className="m-2 rounded-lg flex flex-col">

              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">
                  {t('pages.dsp.packageCard.stopNo')}:
                </span>
                <span className="font-medium">
                  {packageDetail?.deliveryTaskOrder?.stopNumber != null ? formatStopNumber(packageDetail?.deliveryTaskOrder?.stopNumber,packageDetail?.deliveryTaskOrder?.subStopNumber) : '-'}
                </span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">
                  {t('pages.dsp.packageCard.routeNo')}:
                </span>
                <span className="font-medium">
                  {packageDetail.deliveryTaskOrder.routeName || '-'}
                </span>
              </div>
              
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">
                  {t('pages.dsp.packageCard.assignedTime')}:
                </span>
                <span className="text-sm">
                  {packageDetail.deliverAt ? formatTimestampToSecondsTime(packageDetail.deliverAt) : '-'}
                </span>
              </div>
              
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">
                  {t('pages.dsp.packageCard.assignedDriver')}:
                </span>
                <span className="text-sm">
                  {packageDetail.assignedDriver || '-'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {t('pages.dsp.packageCard.deliveryAttempt')}:
                </span>
                <span className="text-sm">
                  {packageDetail.deliveryAttempts} out of 3
                </span>
              </div>
              <OrderNoteItem 
                orderNote={packageDetail?.deliveryTaskOrder?.orderNote}
                currentLanguage={i18n.language}
                className="mt-2"
              />
            </Card>
          )}
        </div>

        {/* 底部固定按钮区域 */}
        <div className="p-4 pt-0 flex gap-3 border-t border-gray-200 flex-shrink-0 bg-white">
          <Button 
            block 
            color="default" 
            onClick={onCancel}
            className="flex-1"
          >
            {t('pages.dsp.packageScan.cancel')}
          </Button>
          <Button 
            block 
            color="primary" 
            onClick={onConfirm}
            className="flex-1 bg-purple-600 border-purple-600"
          >
            {getButtonText()}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DspPackageCard;
